import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAddonPurchase, type AddonPurchaseResult } from '@/features/billing';
import { useAuth } from '@/services/auth';
import type { AddonSku } from '@/services/billing/addon-change';
import { refreshAuthenticatedAccount } from '@/services/db/account/refresh';
import { useFlexStorage } from './use-flex-storage';
import { FLEX_WALLET_QUERY_KEYS } from './use-flex-wallet';

export type OpenStoragePacks = {
  canOpen: boolean;
  stored: number | null;
  refresh: () => Promise<void>;
  open: (sku?: AddonSku) => Promise<AddonPurchaseResult | null>;
};

/**
 * Opens the stored-profile packs modal. After a purchase it refetches the account, whose
 * stored-profile limit the pack changes, and the wallet usage, so the page updates in place.
 * `refresh` does the same after a pack is removed elsewhere.
 */
export function useOpenStoragePacks(): OpenStoragePacks {
  const { account } = useAuth();
  const queryClient = useQueryClient();
  const { canPurchase, openAddon } = useAddonPurchase();
  const flexStorage = useFlexStorage();
  const storage = flexStorage.status === 'ready' ? flexStorage.storage : null;

  // Stable, because the add-on bridge reinstalls itself when it changes.
  const refresh = useCallback(async () => {
    refreshAuthenticatedAccount(queryClient, account.id);
    await Promise.all(FLEX_WALLET_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  }, [queryClient, account.id]);

  const stored = storage?.stored ?? null;
  const limit = storage?.limit ?? null;
  // Stable while the storage holds, so the callers' callbacks are stable too.
  const open = useCallback(
    async (sku?: AddonSku) => {
      if (stored == null || limit == null) return null;
      const result = await openAddon({ request: { key: 'stored-profiles', stored, limit, sku } });
      if (result?.isPurchased !== true) return result;
      await refresh();
      return result;
    },
    [openAddon, refresh, stored, limit],
  );

  return { canOpen: canPurchase && storage != null, stored, refresh, open };
}
